import { useEffect, useRef } from "react";
import "./EInvoiceModal.css";
import ApiService from "../../components/Api_services";
import CustomDropdown from "../../components/customdropdown/CustomDropdown";

const EInvoiceModal = ({
  onClose,
  onConfirm,
  formData,
  onChange,
  errors,
  isSubmitting,
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        const path = event.composedPath ? event.composedPath() : event.path || [];
        // Debug: log path/target for investigation when modal closes
        // (temporary instrumentation)
        // If path exists, check containment
        if (path && path.length) {
          if (modalRef.current && path.includes(modalRef.current)) {
            console.debug("EInvoiceModal: click inside modal - ignored");
            return;
          }
          for (const el of path) {
            if (!el || !el.classList) continue;
            if (
              el.classList.contains("custom-dropdown-menu") ||
              el.classList.contains("custom-dropdown-overlay") ||
              el.classList.contains("custom-dropdown-container") ||
              el.classList.contains("custom-dropdown-selected") ||
              el.classList.contains("einvoice-modal")
            ) {
              console.debug("EInvoiceModal: click on ignored element in path", el);
              return;
            }
          }
        } else {
          const t = event.target;
          if (modalRef.current && modalRef.current.contains(t)) {
            console.debug("EInvoiceModal: target inside modal - ignored", t);
            return;
          }
          if (
            t &&
            t.closest &&
            (t.closest(".custom-dropdown-menu") ||
              t.closest(".custom-dropdown-overlay") ||
              t.closest(".custom-dropdown-container") ||
              t.closest(".custom-dropdown-selected"))
          ) {
            console.debug("EInvoiceModal: target matches ignored selector", t);
            return;
          }
        }
      } catch (err) {
        // ignore and fallback to default behavior
        console.debug("EInvoiceModal: error while checking click path", err);
      }
      // Log what caused the modal to close for debugging
      try {
        const info = {
          target: event.target && event.target.tagName,
          id: event.target && event.target.id,
          className: event.target && event.target.className,
          x: event.clientX,
          y: event.clientY,
        };
        console.debug("EInvoiceModal: closing due to outside click", info);
      } catch (e) {
        // ignore
      }
      onClose();
    };

    // Add event listener on capture to detect earlier
    document.addEventListener("mousedown", handleClickOutside, true);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [onClose]);

  const idTypeOptions = [
    { label: "Identification Card No.", value: "1" },
    { label: "Passport", value: "2" },
    { label: "Business Registration No.", value: "3" },
    { label: "Army No.", value: "4" },
  ];

  const handleIdSearch = async () => {
    if (formData.id_type && formData.id_no) {
      try {
        const data = await ApiService.searchTinById(
          formData.id_type,
          formData.id_no
        );
        let tinNo = "";
        if (
          data.tin &&
          data.tin.http_code === 200 &&
          data.tin.response &&
          data.tin.response.tin
        ) {
          tinNo = data.tin.response.tin;
        } else {
          // 查不到时给默认值
          tinNo = formData.id_type === "2" ? "EI00000000020" : "EI00000000010";
        }
        onChange({ target: { name: "tin_no", value: tinNo } });
      } catch (err) {
        // 查不到也给默认值
        const tinNo = formData.id_type === "2" ? "EI00000000020" : "EI00000000010";
        onChange({ target: { name: "tin_no", value: tinNo } });
        alert("Search failed");
      }
    }
  };

  // 州代码映射
  const stateCodeMap = {
    Johor: "01",
    Kedah: "02",
    Kelantan: "03",
    Melaka: "04",
    "Negeri Sembilan": "05",
    Pahang: "06",
    "Pulau Pinang": "07",
    Perak: "08",
    Perlis: "09",
    Selangor: "10",
    Terengganu: "11",
    Sabah: "12",
    Sarawak: "13",
    "Kuala Lumpur": "14",
    Labuan: "15",
    Putrajaya: "16",
    "Not Applicable": "17",
  };

  // 国家代码映射
  const countryCodeMap = {
    ARUBA: "ABW",
    AFGHANISTAN: "AFG",
    ANGOLA: "AGO",
    ANGUILLA: "AIA",
    "ALAND ISLANDS": "ALA",
    ALBANIA: "ALB",
    ANDORA: "AND",
    "NETHERLANDS ANTILLES": "ANT",
    "UNITED ARAB EMIRATES": "ARE",
    ARGENTINA: "ARG",
    ARMENIA: "ARM",
    "AMERICAN SAMOA": "ASM",
    ANTARCTICA: "ATA",
    "FRENCH SOUTHERN TERRITORIES": "ATF",
    "ANTIGUA AND BARBUDA": "ATG",
    AUSTRALIA: "AUS",
    AUSTRIA: "AUT",
    AZERBAIDJAN: "AZE",
    BURUNDI: "BDI",
    BELGIUM: "BEL",
    BENIN: "BEN",
    "BONAIRE, SINT EUSTATIUS AND SABA": "BES",
    "BURKINA FASO": "BFA",
    BANGLADESH: "BGD",
    BULGARIA: "BGR",
    BAHRAIN: "BHR",
    BAHAMAS: "BHS",
    "BOSNIA AND HERZEGOVINA": "BIH",
    "SAINT BARTHELEMY": "BLM",
    BELARUS: "BLR",
    BELIZE: "BLZ",
    BERMUDA: "BMU",
    BOLIVIA: "BOL",
    BRAZIL: "BRA",
    BARBADOS: "BRB",
    "BRUNEI DARUSSALAM": "BRN",
    BHUTAN: "BTN",
    "BOUVET ISLAND": "BVT",
    BOTSWANA: "BWA",
    "CENTRAL AFRICAN REPUBLIC": "CAF",
    CANADA: "CAN",
    "COCOS ISLAND": "CCK",
    SWITZERLAND: "CHE",
    CHILE: "CHL",
    CHINA: "CHN",
    "COTE D’IVOIRE": "CIV",
    CAMEROON: "CMR",
    "CONGO, THE DEMOCRATIC REPUBLIC": "COD",
    CONGO: "COG",
    "COOK ISLANDS": "COK",
    COLOMBIA: "COL",
    COMOROS: "COM",
    "CAPE VERDE": "CPV",
    "COSTA RICA": "CRI",
    CUBA: "CUB",
    CURACAO: "CUW",
    "CHRISTMAS ISLANDS": "CXR",
    "CAYMAN ISLANDS": "CYM",
    CYPRUS: "CYP",
    "CZECH REPUBLIC": "CZE",
    GERMANY: "DEU",
    DJIBOUTI: "DJI",
    DOMINICA: "DMA",
    DENMARK: "DNK",
    "DOMINICAN REPUBLIC": "DOM",
    ALGERIA: "DZA",
    ECUADOR: "ECU",
    EGYPT: "EGY",
    ERITREA: "ERI",
    "WESTERN SAHARA": "ESH",
    SPAIN: "ESP",
    ESTONIA: "EST",
    ETHIOPIA: "ETH",
    FINLAND: "FIN",
    FIJI: "FJI",
    "FALKLAND ISLANDS (MALVINAS)": "FLK",
    FRANCE: "FRA",
    "FAEROE ISLANDS": "FRO",
    "MICRONESIA, FEDERATED STATES OF": "FSM",
    GABON: "GAB",
    "UNITED KINGDOM": "GBR",
    GEORGIA: "GEO",
    GUERNSEY: "GGY",
    GHANA: "GHA",
    GIBRALTAR: "GIB",
    GUINEA: "GIN",
    GUADELOUPE: "GLP",
    GAMBIA: "GMB",
    "GUINEA-BISSAU": "GNB",
    "EQUATORIAL GUINEA": "GNQ",
    GREECE: "GRC",
    GRENADA: "GRD",
    GREENLAND: "GRL",
    GUATEMALA: "GTM",
    "FRENCH GUIANA": "GUF",
    GUAM: "GUM",
    GUYANA: "GUY",
    "HONG KONG": "HKG",
    "HEARD AND MCDONALD ISLANDS": "HMD",
    HONDURAS: "HND",
    CROATIA: "HRV",
    HAITI: "HTI",
    HUNGARY: "HUN",
    INDONESIA: "IDN",
    "ISLE OF MAN": "IMN",
    INDIA: "IND",
    "BRITISH INDIAN OCEAN TERRITORY": "IOT",
    IRELAND: "IRL",
    IRAN: "IRN",
    IRAQ: "IRQ",
    ICELAND: "ISL",
    ISRAEL: "ISR",
    ITALY: "ITA",
    JAMAICA: "JAM",
    "JERSEY (CHANNEL ISLANDS)": "JEY",
    JORDAN: "JOR",
    JAPAN: "JPN",
    KAZAKHSTAN: "KAZ",
    KENYA: "KEN",
    KYRGYZSTAN: "KGZ",
    CAMBODIA: "KHM",
    KIRIBATI: "KIR",
    "ST.KITTS AND NEVIS": "KNA",
    "THE REPUBLIC OF KOREA": "KOR",
    KUWAIT: "KWT",
    LAOS: "LAO",
    LEBANON: "LBN",
    LIBERIA: "LBR",
    "LIBYAN ARAB JAMAHIRIYA": "LBY",
    "SAINT LUCIA": "LCA",
    LIECHTENSTEIN: "LIE",
    "SRI LANKA": "LKA",
    LESOTHO: "LSO",
    LITHUANIA: "LTU",
    LUXEMBOURG: "LUX",
    LATVIA: "LVA",
    MACAO: "MAC",
    "SAINT MARTIN (FRENCH PART)": "MAF",
    MOROCCO: "MAR",
    MONACO: "MCO",
    "MOLDOVA, REPUBLIC OF": "MDA",
    MADAGASCAR: "MDG",
    MALDIVES: "MDV",
    MEXICO: "MEX",
    "MARSHALL ISLANDS": "MHL",
    "MACEDONIA, THE FORMER YUGOSLAV REPUBLIC OF": "MKD",
    MALI: "MLI",
    MALTA: "MLT",
    MYANMAR: "MMR",
    MONTENEGRO: "MNE",
    MONGOLIA: "MNG",
    "NORTHERN MARIANA ISLANDS": "MNP",
    MOZAMBIQUE: "MOZ",
    MAURITANIA: "MRT",
    MONTSERRAT: "MSR",
    MARTINIQUE: "MTQ",
    MAURITIUS: "MUS",
    MALAWI: "MWI",
    MALAYSIA: "MYS",
    MAYOTTE: "MYT",
    NAMIBIA: "NAM",
    "NEW CALEDONIA": "NCL",
    NIGER: "NER",
    "NORFOLK ISLAND": "NFK",
    NIGERIA: "NGA",
    NICARAGUA: "NIC",
    NIUE: "NIU",
    NETHERLANDS: "NLD",
    NORWAY: "NOR",
    NEPAL: "NPL",
    NAURU: "NRU",
    "NEW ZEALAND": "NZL",
    OMAN: "OMN",
    PAKISTAN: "PAK",
    PANAMA: "PAN",
    PITCAIRN: "PCN",
    PERU: "PER",
    PHILIPPINES: "PHL",
    PALAU: "PLW",
    "PAPUA NEW GUINEA": "PNG",
    POLAND: "POL",
    "PUERTO RICO": "PRI",
    "DEMOC.PEOPLES REP.OF KOREA": "PRK",
    PORTUGAL: "PRT",
    PARAGUAY: "PRY",
    "PALESTINIAN TERRITORY, OCCUPIED": "PSE",
    "FRENCH POLYNESIA": "PYF",
    QATAR: "QAT",
    REUNION: "REU",
    ROMANIA: "ROU",
    "RUSSIAN FEDERATION (USSR)": "RUS",
    RWANDA: "RWA",
    "SAUDI ARABIA": "SAU",
    SUDAN: "SDN",
    SENEGAL: "SEN",
    SINGAPORE: "SGP",
    "SOUTH GEORGIA AND THE SOUTH SANDWICH ISLAND": "SGS",
    "ST. HELENA": "SHN",
    "SVALBARD AND JAN MAYEN ISLANDS": "SJM",
    "SOLOMON ISLANDS": "SLB",
    "SIERRA LEONE": "SLE",
    "EL SALVADOR": "SLV",
    "SAN MARINO": "SMR",
    SOMALIA: "SOM",
    "ST. PIERRE AND MIQUELON": "SPM",
    SERBIA: "SRB",
    "SOUTH SUDAN": "SSD",
    "SAO TOME AND PRINCIPE": "STP",
    SURINAME: "SUR",
    "SLOVAK REPUBLIC": "SVK",
    SLOVENIA: "SVN",
    SWEDEN: "SWE",
    "ESWATINI, KINGDOM OF (SWAZILAND)": "SWZ",
    "SINT MAARTEN (DUTCH PART)": "SXM",
    SEYCHELLES: "SYC",
    "SYRIAN ARAB REPUBLIC": "SYR",
    "TURKS AND CAICOS ISLANDS": "TCA",
    CHAD: "TCD",
    TOGO: "TGO",
    THAILAND: "THA",
    TAJIKISTAN: "TJK",
    TOKELAU: "TKL",
    TURKMENISTAN: "TKM",
    "TIMOR-LESTE": "TLS",
    TONGA: "TON",
    "TRINIDAD AND TOBAGO": "TTO",
    TUNISIA: "TUN",
    TURKIYE: "TUR",
    TUVALU: "TUV",
    TAIWAN: "TWN",
    "TANZANIA UNITED REPUBLIC": "TZA",
    UGANDA: "UGA",
    UKRAINE: "UKR",
    "UNITED STATES MINOR OUTLYING ISLANDS": "UMI",
    URUGUAY: "URY",
    "UNITED STATES OF AMERICA": "USA",
    UZBEKISTAN: "UZB",
    "VATICAN CITY STATE (HOLY SEE)": "VAT",
    "SAINT VINCENT AND GRENADINES": "VCT",
    VENEZUELA: "VEN",
    "VIRGIN ISLANDS(BRITISH)": "VGB",
    "VIRGIN ISLANDS(US)": "VIR",
    VIETNAM: "VNM",
    VANUATU: "VUT",
    "WALLIS AND FUTUNA ISLANDS": "WLF",
    SAMOA: "WSM",
    KOSOVO: "XKX",
    YEMEN: "YEM",
    "SOUTH AFRICA": "ZAF",
    ZAMBIA: "ZMB",
    ZIMBABWE: "ZWE",
  };

  const stateOptions = [
    { label: "Johor", value: "01" },
    { label: "Kedah", value: "02" },
    { label: "Kelantan", value: "03" },
    { label: "Melaka", value: "04" },
    { label: "Negeri Sembilan", value: "05" },
    { label: "Pahang", value: "06" },
    { label: "Pulau Pinang", value: "07" },
    { label: "Perak", value: "08" },
    { label: "Perlis", value: "09" },
    { label: "Selangor", value: "10" },
    { label: "Terengganu", value: "11" },
    { label: "Sabah", value: "12" },
    { label: "Sarawak", value: "13" },
    { label: "Kuala Lumpur", value: "14" },
    { label: "Labuan", value: "15" },
    { label: "Putrajaya", value: "16" },
    { label: "Not Applicable", value: "17" },
  ];
  const countryOptions = Object.entries(countryCodeMap).map(([country, code]) => ({
    label: country === "MALAYSIA" ? "Malaysia" : country,
    value: code,
  }));

  const normalizeStateValue = (value) => {
    if (!value) return "";
    const stringValue = String(value).trim();
    if (/^\d{2}$/.test(stringValue)) return stringValue;
    return stateCodeMap[stringValue] || stringValue;
  };

  const normalizeCountryValue = (value) => {
    if (!value) return "";
    const stringValue = String(value).trim();
    if (/^[A-Z]{3}$/.test(stringValue)) return stringValue;
    const upperValue = stringValue.toUpperCase();
    return countryCodeMap[upperValue] || stringValue;
  };

  const handleIdTypeChange = (selected) => {
    // console.log("Selected id:", selected);
    onChange({
      target: {
        name: "id_type",
        value:
          typeof selected === "object" && selected !== null
            ? selected.value
            : selected,
      },
    });
  };

  const handleStateChange = (selected) => {
    // console.log("Selected state:", selected);
    onChange({
      target: {
        name: "state",
        value:
          typeof selected === "object" && selected !== null
            ? selected.value
            : selected,
      },
    });
  };

  const handleCountryChange = (selected) => {
    // console.log("Selected country:", selected);
    onChange({
      target: {
        name: "country",
        value:
          typeof selected === "object" && selected !== null
            ? selected.value
            : selected,
      },
    });
  };

  return (
    <div className="einvoice-modal-overlay">
      <div className="einvoice-modal" ref={modalRef}>
        <div className="evclose-button">
          <button onClick={onClose}>&#10006;</button>
        </div>

        <div className="einvoice-modal-container">
          <div className="einvoice-modal-header">
            <strong>
              Starting 2025-07-01, we are implementing E-invoice according to
              LHDN requirement.
            </strong>
          </div>
          <div className="einvoice-modal-desc">
            Please fill in your E-invoice Information as below
          </div>
          <form
            className="einvoice-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
              onConfirm(e);
            }}
          >
            <div className="einvoice-form-group">
              <label>
                Id Type <span className="einvoice-required">*</span>
              </label>
              <CustomDropdown
                options={idTypeOptions}
                value={formData.id_type}
                onChange={handleIdTypeChange}
                placeholder="Select Id Type"
                className="einvoicemodal-dropdown"
                popupRef={modalRef}
              />
              {/* {errors.id_type && <div className="einvoice-error-msg">{errors.id_type}</div>} */}
            </div>

            <div className="einvoice-form-group">
              <label>
                {(() => {
                  switch (formData.id_type) {
                    case "1":
                      return (
                        <>
                          Identification Card No{" "}
                          <span className="einvoice-required">*</span>
                        </>
                      );
                    case "2":
                      return (
                        <>
                          Passport <span className="einvoice-required">*</span>
                        </>
                      );
                    case "3":
                      return (
                        <>
                          Business Registration No{" "}
                          <span className="einvoice-required">*</span>
                        </>
                      );
                    case "4":
                      return (
                        <>
                          Army No <span className="einvoice-required">*</span>
                        </>
                      );
                    default:
                      return (
                        <>
                          Identification Card No{" "}
                          <span className="einvoice-required">*</span>
                        </>
                      );
                  }
                })()}
              </label>
              <input
                type="text"
                name="id_no"
                value={formData.id_no || ""}
                onChange={onChange}
                className={errors.id_no ? "einvoice-error" : ""}
              />
              {errors.id_no && (
                <div className="einvoice-error-msg">{errors.id_no}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${
                errors.full_name ? " error" : ""
              }`}
            >
              <label>
                Full Name <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name || ""}
                onChange={onChange}
                className={errors.full_name ? "einvoice-error" : ""}
              />
              {errors.full_name && (
                <div className="einvoice-error-msg">{errors.full_name}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.email ? " error" : ""}`}
            >
              <label>
                Email <span className="einvoice-required">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email || ""}
                onChange={onChange}
                className={errors.email ? "einvoice-error" : ""}
              />
              {errors.email && (
                <div className="einvoice-error-msg">{errors.email}</div>
              )}
            </div>

            <div className={`einvoice-form-group${errors.tel ? " error" : ""}`}>
              <label>
                Tel <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="tel"
                value={formData.tel || ""}
                onChange={onChange}
                className={errors.tel ? "einvoice-error" : ""}
              />
              {errors.tel && (
                <div className="einvoice-error-msg">{errors.tel}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.address ? " error" : ""}`}
            >
              <label>
                Address <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address || ""}
                onChange={onChange}
                className={errors.address ? "einvoice-error" : ""}
              />
              {errors.address && (
                <div className="einvoice-error-msg">{errors.address}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.city ? " error" : ""}`}
            >
              <label>
                City <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city || ""}
                onChange={onChange}
                className={errors.city ? "einvoice-error" : ""}
              />
              {errors.city && (
                <div className="einvoice-error-msg">{errors.city}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.state ? " error" : ""}`}
            >
              <label>
                State <span className="einvoice-required">*</span>
              </label>
              <CustomDropdown
                options={stateOptions}
                value={normalizeStateValue(formData.state)}
                onChange={handleStateChange}
                placeholder="Select State"
                className={
                  errors.state
                    ? "einvoicemodal-dropdown-error"
                    : "einvoicemodal-dropdown"
                }
                popupRef={modalRef}
              />
              {errors.state && (
                <div className="einvoice-error-msg">{errors.state}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${
                errors.postcode ? " error" : ""
              }`}
            >
              <label>
                Postcode <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode || ""}
                onChange={onChange}
                className={errors.postcode ? "einvoice-error" : ""}
              />
              {errors.postcode && (
                <div className="einvoice-error-msg">{errors.postcode}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.country ? " error" : ""}`}
            >
              <label>
                Country <span className="einvoice-required">*</span>{" "}
              </label>
              <CustomDropdown
                options={countryOptions}
                value={normalizeCountryValue(formData.country)}
                onChange={handleCountryChange}
                placeholder="Select Country"
                className={
                  errors.country
                    ? "einvoicemodal-dropdown-error"
                    : "einvoicemodal-dropdown"
                }
                popupRef={modalRef}
              />
              {errors.country && (
                <div className="einvoice-error-msg">{errors.country}</div>
              )}
            </div>

            <div
              className={`einvoice-form-group${errors.tin_no ? " error" : ""}`}
            >
              <label>
                Tin No <span className="einvoice-required">*</span>
              </label>
              <input
                type="text"
                name="tin_no"
                value={formData.tin_no || ""}
                readOnly
                className={errors.tin_no ? "einvoice-error" : ""}
              />
              {errors.tin_no && (
                <div className="einvoice-error-msg">{errors.tin_no}</div>
              )}
              <button
                type="button"
                className="einvoice-id-search-btn"
                onClick={handleIdSearch}
              >
                Search
              </button>
            </div>

            <div className="einvoice-modal-footer">
              <span className="einvoice-modal-note">
                * Please double-check all details before submitting. Changes
                will not be allowed after submission.
              </span>
              <div className="einvoice-modal-actions">
                <button
                  type="button"
                  className="einvoice-btn-cancel"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="einvoice-btn-confirm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Confirm"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EInvoiceModal;
